import type { ReactNode } from 'react';
import { MapPin, Phone, ShoppingBag, User } from 'lucide-react';

// O ESQUELETO ÚNICO DAS FICHAS CPF (Douglas, 16/09/2026).
//
// Portabilidade e Renovação são campanhas diferentes, mas a consultora abre as
// duas no mesmo dia: a ordem dos blocos e a cara deles têm que ser a mesma, para
// ela achar cada coisa no mesmo lugar. Muda o conteúdo, nunca a estrutura:
//
//   1 · Por que ligar        o gancho, escrito pela campanha
//   2 · Gestor / Como falar  com quem se fala — sempre o segundo
//   3 · O que dá para vender aqui   a oferta, só com motivo verdadeiro
//   4 · O que ele tem hoje   base da Vivo (port) ou a conta da Claro (renov)
//   5 · Onde ele mora        endereço e as duas coberturas
//   6 · Quem é               titular, CPF, empresa de origem

export const CARD =
  'rounded-xl border border-border bg-card shadow-[0_1px_2px_rgba(60,20,30,0.05),0_4px_14px_-6px_rgba(60,20,30,0.08)]';
const CABECALHO =
  'flex h-[30px] items-center justify-between gap-2 border-b border-border bg-[#FAF8F4] px-3 dark:bg-[#241A1A]';
export const ROTULO = 'text-[9.5px] font-bold uppercase tracking-wider text-muted-foreground';
export const GRID = 'grid grid-cols-[repeat(auto-fit,minmax(290px,1fr))] gap-3';

export const moeda = (v: number | null | undefined) =>
  v === null || v === undefined ? '—' : v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

export const telefoneFmt = (t: string | number | null | undefined) => {
  const d = String(t ?? '').replace(/\D/g, '');
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return d || '—';
};

export const cpfFmt = (c: string | null | undefined) => {
  const d = String(c ?? '').replace(/\D/g, '');
  return d.length === 11 ? `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}` : (c || '—');
};

export const cnpjFmt = (c: string | null | undefined) => {
  const d = String(c ?? '').replace(/\D/g, '');
  return d.length === 14
    ? `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`
    : (c || '—');
};

export const cepFmt = (c: string | null | undefined) => {
  const d = String(c ?? '').replace(/\D/g, '');
  return d.length === 8 ? `${d.slice(0, 5)}-${d.slice(5)}` : (c || '—');
};

export function Bloco({ titulo, meta, icone, children }: {
  titulo: string; meta?: string; icone?: ReactNode; children: ReactNode;
}) {
  return (
    <section className={CARD}>
      <header className={CABECALHO}>
        <b className="text-[10px] font-bold uppercase tracking-[1.2px]">
          {icone ? <span className="mr-1 inline-flex align-[-2px]">{icone}</span> : null}
          {titulo}
        </b>
        {meta ? <span className="text-[10.5px] text-muted-foreground">{meta}</span> : null}
      </header>
      <div className="flex flex-col gap-3 p-3.5">{children}</div>
    </section>
  );
}

export function Par({ rotulo, valor, title }: { rotulo: string; valor: ReactNode; title?: string }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <span className={ROTULO}>{rotulo}</span>
      <span className="truncate text-[13.5px] font-semibold" title={title}>{valor}</span>
    </div>
  );
}

export function Pares({ children }: { children: ReactNode }) {
  return <div className="grid grid-cols-[repeat(auto-fit,minmax(160px,1fr))] gap-3">{children}</div>;
}

export function Tile({ rotulo, valor, sufixo }: { rotulo: string; valor: string; sufixo?: string }) {
  return (
    <div className="flex flex-col gap-0.5 rounded-lg border border-border bg-[#FAF8F4] px-3 py-2 dark:bg-[#241A1A]">
      <span className="font-display text-[19px] tabular-nums text-primary">
        {valor}
        {sufixo ? <span className="ml-1 text-[12px] font-semibold text-foreground">{sufixo}</span> : null}
      </span>
      <span className={ROTULO}>{rotulo}</span>
    </div>
  );
}

export function Tiles({ children }: { children: ReactNode }) {
  return <div className="grid grid-cols-[repeat(auto-fit,minmax(145px,1fr))] gap-2.5">{children}</div>;
}

// 1 · Por que ligar — o gancho, uma frase que a campanha escreve.
export function BlocoPorQueLigar({ meta, frase, children }: { meta: string; frase: ReactNode; children?: ReactNode }) {
  return (
    <Bloco titulo="Por que ligar" meta={meta}>
      <div className="border-l-[3px] border-l-primary pl-2.5 text-[14.5px] font-semibold">{frase}</div>
      {children}
    </Bloco>
  );
}

// 2 · Gestor e telefones — sempre o segundo bloco, nas duas campanhas.
export function BlocoComoFalar({ master, telefones, email, onLigar, semGestor }: {
  master: Record<string, unknown>;
  telefones: string[];
  email?: string | null;
  onLigar?: (t: string) => void;
  semGestor?: string;
}) {
  const nome = (master.gestor_nome as string) || null;
  const papel = (master.gestor_papel as string) || null;
  const celular = (master.gestor_celular as string) || null;
  const gestorEmail = (master.gestor_email as string) || null;
  const outros = telefones.filter((t) => t !== celular);
  return (
    <Bloco
      titulo="Como falar"
      meta={nome ? `gestor + ${outros.length} telefone${outros.length === 1 ? '' : 's'}` : `${telefones.length} telefone${telefones.length === 1 ? '' : 's'}`}
      icone={<Phone className="h-3 w-3" />}
    >
      {nome ? (
        <div className="flex flex-col gap-1.5 rounded-xl border border-white/[0.07] bg-[linear-gradient(150deg,#5E1424,#3E0E1B)] p-3.5 text-white">
          <span className="text-[9.5px] font-bold uppercase tracking-wider text-white/70">
            Gestor da conta · cadastro Vivo{papel ? ` · ${papel}` : ''}
          </span>
          <span className="text-[17px] font-bold leading-tight">{nome}</span>
          <div className="flex flex-wrap items-center gap-2">
            {celular ? (
              <button
                type="button"
                onClick={() => onLigar?.(celular)}
                className="rounded-lg bg-white/[0.12] px-2.5 py-1.5 text-[12.5px] font-semibold text-white hover:bg-white/[0.2]"
              >
                {telefoneFmt(celular)}
              </button>
            ) : null}
            {gestorEmail ? <span className="text-[12.5px] text-white/80">{gestorEmail}</span> : null}
          </div>
          <span className="text-[11px] text-white/60">É com ele que se liga e se fecha a venda.</span>
        </div>
      ) : (
        <p className="text-[12px] text-muted-foreground">
          {semGestor || 'O cadastro da Vivo não trouxe gestor para este CNPJ. Confirme na ligação com quem falar.'}
        </p>
      )}
      {outros.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {outros.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => onLigar?.(t)}
              className="rounded-lg border border-border px-2.5 py-1.5 text-[12.5px] font-semibold hover:border-primary hover:text-primary"
            >
              {telefoneFmt(t)}
            </button>
          ))}
        </div>
      ) : null}
      {email && email !== gestorEmail ? <Par rotulo="E-mail do lead" valor={email} title={email} /> : null}
    </Bloco>
  );
}

export interface Oferta {
  nome: string;
  valor: number;
  selo: string;
  porque: string;
  destaque?: boolean;
  alternativa?: boolean;      // entra NO LUGAR de outra: não soma no total
}

// 3 · O que dá para vender aqui — mapa, nunca proposta.
export function BlocoOferta({ ofertas, onLevarParaProposta }: {
  ofertas: Oferta[];
  onLevarParaProposta?: (o: Oferta[]) => void;
}) {
  const somam = ofertas.filter((o) => !o.alternativa);
  return (
    <Bloco titulo="O que dá para vender aqui" meta="mapa — não é proposta" icone={<ShoppingBag className="h-3 w-3" />}>
      {ofertas.length === 0 ? (
        <p className="text-[12px] text-muted-foreground">
          Sem oferta com motivo confirmado ainda. A ligação e a fatura dizem o que cabe aqui.
        </p>
      ) : (
        <>
          <div className="grid grid-cols-[repeat(auto-fit,minmax(215px,1fr))] gap-2.5">
            {ofertas.map((o) => (
              <div
                key={o.nome}
                className={
                  o.destaque
                    ? 'flex flex-col gap-1.5 rounded-xl border border-white/[0.08] bg-[linear-gradient(150deg,#5E1424,#3E0E1B)] p-3 text-white'
                    : 'flex flex-col gap-1.5 rounded-xl border border-border bg-card p-3'
                }
              >
                <span
                  className={
                    o.destaque
                      ? 'self-start rounded-full border border-white/[0.22] px-2 py-0.5 text-[9.5px] font-bold uppercase tracking-wider text-white/85'
                      : 'self-start rounded-full border border-border px-2 py-0.5 text-[9.5px] font-bold uppercase tracking-wider text-muted-foreground'
                  }
                >
                  {o.selo}
                </span>
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-[14px] font-bold">{o.nome}</span>
                  <span className="whitespace-nowrap font-display text-[17px] tabular-nums">{moeda(o.valor)}</span>
                </div>
                <p className={o.destaque ? 'text-[11.5px] text-white/75' : 'text-[11.5px] text-muted-foreground'}>
                  {o.porque}
                </p>
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={() => onLevarParaProposta?.(somam)}
            className="self-start rounded-lg bg-[linear-gradient(#4A1320,#5B0A0D)] px-3 py-2 text-[11px] font-bold text-white"
          >
            Levar para a aba Proposta
          </button>
          <p className="text-[12px] text-muted-foreground">
            Isto não cria proposta nenhuma: é o que cabe neste cliente. A proposta continua sendo montada na aba
            Proposta — o botão só leva estes itens para lá, já preenchidos.
          </p>
        </>
      )}
    </Bloco>
  );
}

// 5 · Onde ele mora — endereço e as DUAS coberturas, com aviso quando discordam.
export function BlocoOndeMora({ endereco, cidade, uf, cep, coberturaBase, coberturaNossa, enderecoNovo, fonte }: {
  endereco?: string | null;
  cidade?: string | null;
  uf?: string | null;
  cep?: string | null;
  coberturaBase?: boolean | null;
  coberturaNossa?: boolean | null;
  enderecoNovo?: string | null;
  fonte: string;
}) {
  const diverge = coberturaBase != null && coberturaNossa != null && coberturaBase !== coberturaNossa;
  const texto = (v?: boolean | null) => (v == null ? '—' : v ? 'Tem fibra' : 'Sem fibra');
  return (
    <Bloco titulo="Onde ele mora" meta={fonte} icone={<MapPin className="h-3 w-3" />}>
      <Pares>
        <Par rotulo="Endereço" valor={endereco || '—'} title={endereco || undefined} />
        <Par rotulo="Cidade · UF" valor={[cidade, uf].filter(Boolean).join(' — ') || '—'} />
        <Par rotulo="CEP" valor={cepFmt(cep)} />
        <Par rotulo="Cobertura na base" valor={texto(coberturaBase)} />
        <Par rotulo="Cobertura na nossa consulta" valor={texto(coberturaNossa)} />
        {enderecoNovo ? <Par rotulo="Mudou de endereço" valor={enderecoNovo} title={enderecoNovo} /> : null}
      </Pares>
      {diverge ? (
        <p className="text-[12px] font-semibold text-primary">
          A base e a nossa consulta discordam sobre a cobertura deste CEP. Confirme antes de prometer fibra.
        </p>
      ) : null}
    </Bloco>
  );
}

// 6 · Quem é — titular, CPF e a empresa de origem.
export function BlocoQuemE({ meta, children }: { meta: string; children: ReactNode }) {
  return (
    <Bloco titulo="Quem é" meta={meta} icone={<User className="h-3 w-3" />}>
      <Pares>{children}</Pares>
    </Bloco>
  );
}
